import * as React from 'react';
import { useFormContext, useController } from 'react-hook-form';
import styled from 'styled-components';
import { PhoneInput, defaultCountries, parseCountry } from 'react-international-phone';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { countryAndIsoCodesForMobileNumber, mobileNumberCountryCodesInISO } from '../../core/constants';
import FieldDecorator from '../fields/FieldDecorator';
import { FieldProps, generateValidations } from '../../core/form';
// eslint-disable-next-line import/extensions, import/no-unresolved
import 'react-international-phone/style.css';
import { getUserOfficeCountry } from '../../lib/utils';
import { blackColour, borderGrey, errorRed, popupZIndex, whiteBackground } from '../../appComponents/cssConstants';
import { hasValue } from '../../lib/lib';

const MobileNumberField = ({ name, label, readOnly, disabled, required }: FieldProps) => {
	const {
		control,
		setValue,
		formState: { errors }
	} = useFormContext();
	const { field } = useController({
		name,
		control,
		rules: generateValidations(getPhoneNumberValidationProps(required))
	});
	const { value } = field;

	const [userClearInputValue, setUserClearInputValue] = React.useState(false);
	const [isBlur, setIsBlur] = React.useState(false);

	const handleValueChange = (internationalMobile: string) => {
		// Remove leading zero after country code for countries where users commonly type it
		// AU: 04xx → +614xx, UK: 07xx → +447xx, IE: 08x → +3538x, PH: 09xx → +639xx
		const leadingZeroPatterns = [
			{ prefix: '+610', replacement: '+61' }, // Australia
			{ prefix: '+440', replacement: '+44' }, // United Kingdom
			{ prefix: '+3530', replacement: '+353' }, // Ireland
			{ prefix: '+630', replacement: '+63' } // Philippines
		];

		const matchedPattern = leadingZeroPatterns.find(({ prefix }) => internationalMobile.startsWith(prefix));
		const formattedMobile = matchedPattern
			? internationalMobile.replace(matchedPattern.prefix, matchedPattern.replacement)
			: internationalMobile;

		setValue(name, formattedMobile);
		setUserClearInputValue(!hasValue(formattedMobile));
	};

	return (
		<FieldWrapper name={name} errors={errors} label={label} $normalLabel={userClearInputValue && isBlur}>
			<StyledPhoneInput
				countries={countries}
				defaultCountry={getDefaultCountry()}
				value={value ?? ''}
				onChange={handleValueChange}
				onFocus={() => setIsBlur(false)}
				onBlur={() => setIsBlur(true)}
				className="phone-input-container"
				inputProps={{ name, readOnly, disabled }}
				inputClassName="phone-number-input"
				style={getStylesVariables(hasValue(errors) && !!errors[name])}
				countrySelectorStyleProps={{
					flagClassName: 'country-selector-flag',
					dropdownArrowClassName: 'country-selector-dropdown-arrow',
					dropdownStyleProps: { className: 'countries-dropdown-container' }
				}}
			/>
		</FieldWrapper>
	);
};

export default MobileNumberField;

const countries = defaultCountries.filter((country) => {
	const { iso2 } = parseCountry(country);
	return mobileNumberCountryCodesInISO.includes(iso2);
});

const getDefaultCountry = () =>
	countryAndIsoCodesForMobileNumber.find((x) => x.country === getUserOfficeCountry())?.isoCode;

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
	try {
		return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
	} catch {
		return false;
	}
};

const getPhoneNumberValidationProps = (required: boolean) => {
	const validate = (value: string) =>
		!hasValue(value) || isPhoneValid(value) || 'Please provide a valid mobile phone number.';
	return { required, validate };
};

const FieldWrapper = styled(FieldDecorator)<{ $normalLabel: boolean }>`
	position: relative;

	> label {
		display: block;
		margin: 0;
		font-size: 16px;
		transform-origin: top left;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: calc(100% - 24px);
		position: absolute;
		left: 74px;
		top: 0;
		transform: ${(props) =>
			props.$normalLabel ? 'translate(12px, 13px) scale(1)' : 'translate(12px, 4px) scale(0.75)'};
		transition:
			color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
			transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
			max-width 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
		z-index: 1;
		pointer-events: auto;
		user-select: none;
	}
`;

const getStylesVariables = (hasError: boolean) => ({
	'--react-international-phone-height': '50px',
	'--react-international-phone-border-radius': '12px',
	'--react-international-phone-text-color': blackColour,
	'--react-international-phone-font-size': '16px',
	'--react-international-phone-border-color': hasError ? errorRed : borderGrey,

	'--react-international-phone-flag-width': '36px',
	'--react-international-phone-flag-height': '36px',
	'--react-international-phone-country-selector-arrow-size': '5px',

	'--react-international-phone-dropdown-item-font-size': '16px',
	'--react-international-phone-dropdown-top': '50px',

	'--react-international-phone-background-color': whiteBackground
});

const StyledPhoneInput = styled(PhoneInput)`
	.country-selector-flag {
		margin-left: 12px; // Same as the form input padding-left
		margin-right: 8px;
	}

	.country-selector-dropdown-arrow {
		margin-right: 8px;
	}

	.countries-dropdown-container {
		outline: ${borderGrey} solid 1px;
		border-radius: 12px;
		z-index: ${popupZIndex};
	}

	.phone-number-input {
		width: 100%;
		padding-top: 21px;
		padding-right: 12px;
		padding-bottom: 4px;
		padding-left: 12px;
	}
`;
